import { StandardProps } from "@/components/componentMap";
import {
  ensureIsActive,
  ensureIsActiveVariant,
} from "@/utils/product/ensureIsActive";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { isAvailableToPreOrder } from "@/utils/variant/isAvailableToPreOrder";
import { isInStoreOnly } from "@/utils/variant/isInStoreOnly";
import { NotAvailableUI } from "./NotAvailableUI";
import { isFutureRelease } from "@/utils/variant/isFutureRelease";
import { hasAnyStock } from "@/utils/product/hasAnyStock";
import { NotifiableEvent } from "@/types/types";
import { CustomerNotifiableEvents } from "@/types/prodcatBaseTypes";
import { hasAvailableVariantsMatchingColour } from "@/utils/product/hasAvailableVariantsMatchingColour";

export const NotAvailable = ({
  product,
  runtimeConfig,
  configProps,
}: Pick<StandardProps, "product" | "configProps" | "runtimeConfig">) => {
  const activeProduct = ensureIsActive(product);
  const { variant, isSelected } = useVariant(product);
  const activeVariant = ensureIsActiveVariant(variant);
  const showOutOfStockExperienceInfo =
    configProps?.showOosExperienceForMultiVariant === true ||
    (!hasAnyStock(product) &&
      !isFutureRelease(variant) &&
      product.variants.length === 1);

  if (!activeVariant || !activeProduct) {
    return null;
  }

  const notifiableEventType = getNotifiableEventType(
    activeVariant.customerNotifiableEvents,
  );

  const shouldEnableAddToBasket = isSelected
    ? activeVariant?.availability.availableToOrder
    : hasAvailableVariantsMatchingColour(
        activeProduct,
        activeVariant?.differentiators?.colour,
      );

  if (shouldEnableAddToBasket) {
    return null;
  }

  return (
    <NotAvailableUI
      isInStoreOnly={isInStoreOnly(variant)}
      stockApiBaseUrl={runtimeConfig.stockApiBaseUrl}
      stockApiKey={runtimeConfig.stockApiKey}
      storeStockApiKey={runtimeConfig.storeStockApiKey}
      isPreOrderable={isAvailableToPreOrder(variant)}
      storeStockSkuId={activeVariant.aliases.skuId}
      storeStockTitle={activeVariant.title}
      storeStockModalApiUrl={runtimeConfig.storeStockModalApiUrl}
      storeStockScriptUrl={runtimeConfig.storeStockScriptUrl}
      isFutureRelease={isFutureRelease(variant)}
      isVariantSelected={isSelected}
      storeStockDisplayCode={variant.displayCode}
      pimProductId={product.aliases.pimProductId}
      notifiableEventType={notifiableEventType}
      notificationsApiBaseUrl={runtimeConfig.notificationApiBaseUrl}
      notificationsApiKey={runtimeConfig.notificationApiKey}
      showOutOfStockExperienceInfo={showOutOfStockExperienceInfo}
      showNotAvailableInTwoCols={
        configProps?.showNotAvailableInTwoCols === true
      }
    />
  );
};

export function getNotifiableEventType(
  customerNotifiableEvents: CustomerNotifiableEvents,
) {
  const allNotifiableEventTypesInPriorityOrder: NotifiableEvent[] = [
    "backInStock",
    "onRelease",
    "onPreorderLaunch",
    "newlyInStock",
  ];
  const notifiableEvents = allNotifiableEventTypesInPriorityOrder.find(
    (eventType) => customerNotifiableEvents[eventType],
  );

  return notifiableEvents;
}
